import { createSelector } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';

import { InquiryAnalyticsQueryParams } from 'dashboard/models/analytics-query-template';
import { MetricsFilterParams } from 'dashboard/redux/actions/metrics-inquiry';
import {
  DynamicFilterOptionKey,
  DynamicFilterOptions,
} from 'dashboard/redux/reducers/metrics-inquiry';
import { getAdjustedFiltersForSelectors } from 'dashboard/redux/selectors/metrics';
import { RootState } from 'dashboard/redux/store';
import { FilterDimensions } from 'dashboard/views/DashboardInquiryAnalyticsVolumeV2View/InquiryMetrics';

const getInquiryFilters = (state: RootState) => state.metricsInquiry.filters;

export const getAdjustedFilters = getAdjustedFiltersForSelectors<MetricsFilterParams>([
  getInquiryFilters,
]);

const adjustFilterOptions = (options: string[]) =>
  [...options].sort().map((option) => ({ label: option, value: option }));

const getFilterOptionsByKey = (state: RootState, key: keyof DynamicFilterOptions) =>
  state.metricsInquiry.filterOptions[key];

const createAdjustedFilterOptionSelector = (key: keyof DynamicFilterOptions) =>
  createSelector((state: RootState) => getFilterOptionsByKey(state, key), adjustFilterOptions);

export const getDynamicFilters = createSelector(
  [
    createAdjustedFilterOptionSelector(DynamicFilterOptionKey.CountryCodes),
    createAdjustedFilterOptionSelector(DynamicFilterOptionKey.DeviceGroups),
    createAdjustedFilterOptionSelector(DynamicFilterOptionKey.Platforms),
  ],
  (countryCodes, deviceGroups, platForms) => ({
    [FilterDimensions.CountryCode]: countryCodes,
    [FilterDimensions.DeviceGroup]: deviceGroups,
    [FilterDimensions.PlatformIntegration]: platForms,
  }),
);

export const getSelectedQueryTemplate = (state: RootState) =>
  state.metricsInquiry.selectedQueryTemplate;

export const getIsSelectedQueryTemplateDifferentFromFilters = createSelector(
  [getSelectedQueryTemplate, getInquiryFilters],
  (selectedQueryTemplate, filters) => {
    if (selectedQueryTemplate.template == null) {
      return false;
    }
    const {
      attributes: { query },
    } = selectedQueryTemplate.template;
    const queryParams = query as InquiryAnalyticsQueryParams;

    return (
      !isEqual(
        [...queryParams['template-ids'], ...queryParams['inquiry-template-ids']],
        filters.templateOrInquiryTemplateIds,
      ) ||
      !isEqual(queryParams['country-codes'], filters[DynamicFilterOptionKey.CountryCodes]) ||
      !isEqual(queryParams['device-groups'], filters[DynamicFilterOptionKey.DeviceGroups]) ||
      !isEqual(queryParams['platforms'], filters[DynamicFilterOptionKey.Platforms]) ||
      !isEqual(queryParams['group-by'], filters.groupBy) ||
      !isEqual(queryParams['tag-ids'], filters.inquiryTagIds) ||
      selectedQueryTemplate.computedOffsetStart !== filters.createdAtStart ||
      selectedQueryTemplate.computedOffsetEnd !== filters.createdAtEnd
    );
  },
);
